import { Logger, LogSender } from '../../logger';
import { DefaultPingResponse } from './default/Messages';
import Streaming from './Streaming';
import StreamingError, { StreamingErrorCodes } from './StreamingError';
import {
  DoryMessageType,
  DoryServiceType,
  DoryStreamingMessage,
} from './StreamingMessages';
import StreamingService from './StreamingService';

//#region Interfaces
export type IStreamingStatus = {
  payload: {
    status: {
      marketDepth: string;
      news: string;
      quote: string;
      [key: string]: string;
    };
  };
} & DoryStreamingMessage;

export type IStreamingMonitorOptions = {
  interval?: number;
  timeout?: number;
  retryCount?: number;
  sleepTolerance?: number;
  visibilityTimeout?: number;
};
//#endregion

type disconnectListener = (error: StreamingError) => void;
type visibilityListener = (hidden: boolean) => void;

export default class StreamingMonitor {
  private client: Streaming;

  private monitors: {
    running: boolean;
    sleep?: () => void;
    websocket?: () => void;
    status?: () => void;
    internet?: () => void;
  } = {
    running: false,
    sleep: undefined,
    websocket: undefined,
    status: undefined,
    internet: undefined,
  };

  //#region Fields
  private interval: number;
  /**
   * Returns interval in which heartbeat is send (in ms)
   */
  public get Interval(): number {
    return this.interval;
  }
  public set Interval(newVal: number) {
    this.interval = newVal;
    Logger.info(
      LogSender.STREAMING_MONITOR,
      `new monitor interval is ${newVal}ms`
    );
  }

  private timeout: number;
  /**
   * Returns timeout for heartbeat (in ms)
   */
  public get Timeout(): number {
    return this.timeout;
  }
  public set Timeout(newVal: number) {
    this.timeout = newVal;
    Logger.info(
      LogSender.STREAMING_MONITOR,
      `new monitor timeout is ${newVal}ms`
    );
  }

  private retryCount: number;
  /**
   * Returns retry count
   */
  public get RetryCount(): number {
    return this.retryCount;
  }
  public set RetryCount(newVal: number) {
    this.retryCount = newVal;
    Logger.info(
      LogSender.STREAMING_MONITOR,
      `new monitor retryCount is ${newVal}`
    );
  }

  private sleepTolerance: number;
  /**
   * Returns retry count
   */
  public get SleepTolerance(): number {
    return this.sleepTolerance;
  }
  public set SleepTolerance(newVal: number) {
    this.sleepTolerance = newVal;
    Logger.info(
      LogSender.STREAMING_MONITOR,
      `new monitor sleepTolerance is ${newVal}`
    );
  }

  private visibilityTimeout: number;

  public get VisibilityTimeout(): number {
    return this.visibilityTimeout;
  }
  public set VisibilityTimeout(newVal: number) {
    this.visibilityTimeout = newVal;
    Logger.info(
      LogSender.STREAMING_MONITOR,
      `new monitor visibilityTimeout is ${newVal}`
    );
  }

  // To prevent attaching mutliple visibility listener to DOM.
  private isVisibilityEventAttached = false;
  //#endregion

  constructor(client: Streaming) {
    this.client = client;
  }

  public start(
    options: IStreamingMonitorOptions,
    services: StreamingService<unknown>[]
  ): void {
    if (this.monitors.running) {
      Logger.warn(
        LogSender.STREAMING_MONITOR,
        'Did not start Streaming Monitor, monitor is already running'
      );
      return;
    }

    this.interval = options.interval || 2500;
    this.timeout = options.timeout || 1000;
    this.retryCount = options.retryCount || 2;
    this.sleepTolerance = options.sleepTolerance || 5000;
    this.visibilityTimeout = options.visibilityTimeout || 10000;

    this.monitors.running = true;
    this.monitorSleep();
    this.monitorWebsocket();
    this.monitorStatus(services);
    this.monitorInternet();

    if (this.client.isVisibilityMonitorEnabled) {
      Logger.verbose(
        LogSender.STREAMING_MONITOR,
        'Started screen visibility monitor'
      );
      this.monitorScreenVisibility();
    }

    Logger.info(
      LogSender.STREAMING_MONITOR,
      'Started monitoring',
      `\nInterval: ${this.interval}ms`,
      `\nTimeout: ${this.timeout}ms`,
      `\nRetryCount: ${this.retryCount}`
    );
  }

  /**
   * Works by attaching event listeners to document visibilitychange. Don't rely on status information
   * provided by this monitor. Added to toggle websocket connection based on the page visibility,
   */
  private monitorScreenVisibility() {
    if (!this.isVisibilityEventAttached) {
      let visibilityTimer: ReturnType<typeof setTimeout>;
      const handler = () => {
        if (document.visibilityState !== 'visible') {
          Logger.warn(LogSender.STREAMING_MONITOR, 'visibility: Hidden');
          visibilityTimer = setTimeout(() => {
            this.notifyVisibility(true, true);
          }, this.visibilityTimeout);
        } else {
          clearTimeout(visibilityTimer);
          Logger.warn(LogSender.STREAMING_MONITOR, 'visibility: Visible');
          this.notifyVisibility(false);
        }
      };
      document.addEventListener('visibilitychange', handler);
      this.isVisibilityEventAttached = true;
    }
  }

  /**
   * Works by attaching event listeners to navigator.onLine. Don't rely on status information
   * provided by this monitor. Added to gather more debugging information,
   */
  private monitorInternet() {
    Logger.verbose(LogSender.STREAMING_MONITOR, 'Started internet monitor');
    const handler = (e: Event) => {
      Logger.warn(
        LogSender.STREAMING_MONITOR,
        `Internet connection status (navigator.onLine) changed status to ${e.type}`
      );
    };
    window.addEventListener('offline', handler);
    window.addEventListener('online', handler);

    this.monitors.internet = () => {
      window.removeEventListener('offline', handler);
      window.removeEventListener('online', handler);
    };
  }

  private monitorSleep() {
    Logger.verbose(LogSender.STREAMING_MONITOR, 'Started sleep monitor');
    let lastTime = new Date().getTime();
    // if the pc is asleep the execution of setInterval is paused so we can
    //  track the time between interval to check for wake-up
    const intervalId = setInterval(() => {
      const currentTime = new Date().getTime();
      // Added sleep tolerance to ignore code execution delays
      if (currentTime > lastTime + this.interval + this.sleepTolerance) {
        lastTime = currentTime;
        if (!this.client.isServerAvailable && !this.client.isConnected) {
          Logger.warn(
            LogSender.STREAMING_MONITOR,
            'Sleep Monitor: Disconnected'
          );
          this.notify(
            new StreamingError(
              'StreamingMonitor.monitorSleep',
              StreamingErrorCodes.CONNECTION_LOST,
              'Disconnected during sleep'
            )
          );
        }
      } else {
        lastTime = currentTime;
      }
    }, this.interval);

    this.monitors.sleep = () => {
      clearInterval(intervalId);
    };
  }

  /**
   * Start websocket monitor
   *
   * Uses two different methods to detect if the connection to WSS is stable:
   * - monitors all traffic on Socket, if traffic is detected in {interval}ms connection is ok
   * - send {tryCount} pings with {interval}ms time between each one, if ping returns in {timeout}ms connection is ok
   */
  private monitorWebsocket() {
    Logger.verbose(LogSender.STREAMING_MONITOR, 'Started connection monitor');
    // this flag will be flipped if the monitor detects WS traffic in during the {interval}ms
    let connectionBusy = false;
    // this flag is used to toggle the subscription
    let monitorActive = true;

    this.monitors.websocket = this.client.EventBus.all(() => {
      if (monitorActive) {
        connectionBusy = true;
      }
    });
    Logger.silly(LogSender.STREAMING_MONITOR, 'Started EventBus listener');

    // recursive helper method to handle check logic without setInterval
    const checkWSActivity = () => {
      // exit recursion if monitoring is not running
      if (!this.monitors.running) return;

      // Wait for {interval}ms for traffic on Socket
      window.setTimeout(async () => {
        if (!connectionBusy) {
          Logger.silly(
            LogSender.STREAMING_MONITOR,
            'No traffic detected during inspection interval'
          );

          Logger.silly(LogSender.STREAMING_MONITOR, 'Pause EventBus listener');
          monitorActive = false;

          this.sendPing()
            .then(() => {
              if (!this.monitors.running) return;
              Logger.silly(
                LogSender.STREAMING_MONITOR,
                'Resume EventBus listener'
              );
              monitorActive = true;
              checkWSActivity();
            })
            .catch((err: StreamingError) => {
              Logger.warn(
                LogSender.STREAMING_MONITOR,
                'WebSocket Monitor: Disconnected'
              );
              this.notify(err);
            });
        } else {
          Logger.silly(
            LogSender.STREAMING_MONITOR,
            'Traffic detected during inspection interval'
          );
          connectionBusy = false;
          checkWSActivity();
        }
      }, this.interval);
    };
    checkWSActivity();
  }

  private monitorStatus(services: StreamingService<unknown>[]) {
    Logger.verbose(LogSender.STREAMING_MONITOR, 'Started status monitor');
    this.monitors.status = this.client.EventBus.on(
      DoryServiceType.DEFAULT,
      DoryMessageType.DSSTATUS,
      (data: IStreamingStatus) => {
        Logger.verbose(LogSender.STREAMING_MONITOR, 'received status update');
        this.updateStatus(services, data);
      }
    ).bind(this);
  }

  /**
   * Stop Heartbeat, e.g. when client disconnects
   */
  public stop(abort = false): void {
    this.monitors.running = false;
    if (this.monitors.sleep) {
      this.monitors.sleep();
      this.monitors.sleep = undefined;
      Logger.verbose(LogSender.STREAMING_MONITOR, 'Stopped sleep monitor');
    }
    if (this.monitors.websocket) {
      this.monitors.websocket();
      this.monitors.websocket = undefined;
      Logger.verbose(LogSender.STREAMING_MONITOR, 'Stopped connection monitor');
    }
    if (this.monitors.status) {
      this.monitors.status();
      this.monitors.status = undefined;
      Logger.verbose(LogSender.STREAMING_MONITOR, 'Stopped status monitor');
    }
    if (this.monitors.internet) {
      this.monitors.internet();
      this.monitors.internet = undefined;
      Logger.verbose(LogSender.STREAMING_MONITOR, 'Stopped internet monitor');
    }
    Logger.verbose(
      LogSender.STREAMING_MONITOR,
      `${abort ? 'Aborted' : 'Stopped'} monitoring`
    );
  }

  //#region Helpers
  private async sendPing(retryCount = 0): Promise<void> {
    // exit conditions
    if (!this.monitors.running) return;
    const timeout = this.timeout * (retryCount + 1);
    Logger.verbose(
      LogSender.STREAMING_MONITOR,
      `Send monitor ping ${retryCount + 1} of ${
        this.retryCount
      }, using ${timeout}ms timeout`
    );

    try {
      await new Promise<void>((resolve, reject) => {
        const cancel = setTimeout(() => {
          if (!this.monitors.running) return;
          reject(
            new StreamingError(
              'StreamingMonitor.sendPing',
              StreamingErrorCodes.TIMEOUT,
              `Received no message from WebSocket in ${timeout}ms!`
            )
          );
        }, timeout);
        const start = Date.now();
        this.client.ping((data: DefaultPingResponse, err) => {
          clearTimeout(cancel);
          if (!this.monitors.running) return;

          if (err) {
            reject(
              new StreamingError(
                'StreamingMonitor.sendPing',
                StreamingErrorCodes.TIMEOUT,
                'WebSocket ping response timed out!'
              )
            );
          } else if (data.payload.status !== 200) {
            reject(
              new StreamingError(
                'StreamingMonitor.sendPing',
                StreamingErrorCodes.UNKNOWN_STATUS,
                'WebSocket responded with unknown status!'
              )
            );
          } else if (data.payload.message !== 'Ping successful.') {
            reject(
              new StreamingError(
                'StreamingMonitor.sendPing',
                StreamingErrorCodes.UNKNOWN_MESSAGE,
                'WebSocket responded with unknown message!'
              )
            );
          } else {
            Logger.silly(
              LogSender.STREAMING_MONITOR,
              `received monitoring ping response in ${Date.now() - start}ms`
            );
            resolve();
          }
        });
      });
    } catch (err) {
      Logger.warn(LogSender.STREAMING_MONITOR, err as StreamingError);

      if (retryCount + 1 < this.retryCount) {
        return this.sendPing(retryCount + 1);
      } else {
        return Promise.reject(err);
      }
    }
  }
  private updateStatus(
    services: StreamingService<unknown>[],
    data: IStreamingStatus
  ) {
    const status = data.payload.status;
    const unavailableServices: string[] = [];
    const capitalize = (input: string) =>
      `${input[0].toUpperCase()}${input.slice(1)}`;

    services.forEach((service: StreamingService<unknown>) => {
      if (status[service.SERVICENAME]) {
        service.isAvailable = status[service.SERVICENAME] === 'available';
        if (!service.isAvailable) {
          unavailableServices.push(capitalize(service.SERVICENAME));
        }
      }
    });

    if (unavailableServices.length > 0) {
      const err = new StreamingError(
        'StreamingMonitor.updateStatus',
        StreamingErrorCodes.SERVICE_UNAVAILABLE,
        `Following service${
          unavailableServices.length === 1 ? ' is' : 's are'
        } no longer available:\n- ${unavailableServices.join('\n- ')}`
      );
      Logger.warn(LogSender.STREAMING_MONITOR, err);
      this.notify(err, false);
    }
  }
  private notify(err: StreamingError, stopMonitor = true) {
    if (stopMonitor) {
      this.stop(true);
    }

    Logger.warn(LogSender.STREAMING_MONITOR, err);
    for (const listener of this.disconnectListeners) {
      try {
        listener(err);
      } catch (e) {
        Logger.error(
          LogSender.STREAMING,
          `call to disconnectListener failed`,
          e
        );
      }
    }
  }

  private notifyVisibility(hidden: boolean, stopMonitor = false) {
    if (stopMonitor) {
      this.stop(true);
    }

    for (const listener of this.visibilityListener) {
      try {
        listener(hidden);
      } catch (e) {
        Logger.error(
          LogSender.STREAMING,
          `call to disconnectListener failed`,
          e
        );
      }
    }
  }

  private disconnectListeners: disconnectListener[] = [];

  /**
   * Register a callback function to get informed when the connection is lost.
   *
   * @param listener The function that receives the notification
   */
  public addDisconnectListener(listener: disconnectListener): void {
    this.disconnectListeners.push(listener);
  }

  /**
   * Deregister a connection lost Listener
   *
   * @param listener The EventListener function of the event handler to remove from the event target.
   */
  public removeDisconnectListener(listener: disconnectListener): void {
    const filterListeners = (l: disconnectListener) => l !== listener;

    this.disconnectListeners = this.disconnectListeners.filter(filterListeners);
  }

  private visibilityListener: visibilityListener[] = [];

  /**
   * Register a callback function to get informed when the page visibility change.
   *
   * @param listener The function that receives the notification
   */
  public addVisibilityListener(listener: visibilityListener): void {
    this.visibilityListener.push(listener);
  }

  /**
   * Deregister a page visibility Listener
   *
   * @param listener The EventListener function of the event handler to remove from the event target.
   */
  public removeVisibilityListener(listener: visibilityListener): void {
    const filterListeners = (l: visibilityListener) => l !== listener;

    this.visibilityListener = this.visibilityListener.filter(filterListeners);
  }

  //#endregion
}
